/**
 * `extractOutline(body)` — pull the heading tree out of a markdown body
 * (UX-reconciliation F4.1, fed into `<EditorMetaSidecar>`'s Outline section).
 *
 * Pure transform: `string` (markdown body) → `OutlineEntry[]`. No I/O, no React state.
 * Cheap enough to call on every body change for a typical note (a few hundred chars
 * to a few KB) — memoize at the caller for long bodies.
 *
 * Recognizes ATX-style headings (`#` through `######`) at column zero. Setext-style
 * headings (`=====` / `-----` underlines) are deliberately NOT recognized — they're
 * rare in the corpus and the standalone's Outline spec uses ATX-only.
 *
 * Edge cases handled:
 *   - Headings inside fenced code blocks (```...```) are ignored.
 *   - Lines like `#foo` (no space after `#`) are treated as text, not headings.
 *   - Heading levels > 6 are capped at 6 (markdown spec).
 *
 * Each entry's `line` is the 1-based line number in the source body — usable for
 * scroll-to-heading actions in a later iteration.
 */

export interface OutlineEntry {
  /** 1..6 — the heading level. */
  level: number;
  /** The visible heading text (trimmed; leading hashes stripped). */
  text: string;
  /** 1-based line number in the source body. */
  line: number;
}

const HEADING_RE = /^(#{1,6})[ \t]+(.+?)[ \t]*#*[ \t]*$/;
const CODE_FENCE_RE = /^(?:```|~~~)/;

export function extractOutline(body: string): OutlineEntry[] {
  if (body.length === 0) return [];
  const out: OutlineEntry[] = [];
  const lines = body.split('\n');
  let inFence = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (CODE_FENCE_RE.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const match = HEADING_RE.exec(line);
    if (match === null) continue;
    const level = Math.min(6, match[1].length);
    const text = match[2].trim();
    if (text.length === 0) continue;
    out.push({ level, text, line: i + 1 });
  }
  return out;
}
